import React, { useState, useEffect } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup
} from 'react-simple-maps';
import api from '../services/api';
import './Dashboard.css';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const Dashboard = () => {
  const [stats, setStats] = useState({
    licenses: { total: 0, active: 0, expiring: 0, expired: 0 },
    pages: { total: 0, published: 0, drafts: 0 },
    content: { total: 0 },
    loading: true,
  });
  const [topCountries, setTopCountries] = useState([]);
  const [visitorCount, setVisitorCount] = useState({ today: 0, total: 0 });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedUserType, setSelectedUserType] = useState('active');
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });
  const [totalUsers, setTotalUsers] = useState(0);
  const [pendingTickets, setPendingTickets] = useState(0);

  useEffect(() => {
    loadDashboardData();
    // Set up real-time visitor count updates every 30 seconds
    const visitorInterval = setInterval(() => {
      loadVisitorCount();
    }, 30000);
    return () => clearInterval(visitorInterval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const licenseStats = await api.getLicenseStatistics();
      const countriesResponse = await api.getTopCountries();
      await loadVisitorCount();
      
      // Fetch open tickets count
      try {
        const ticketStats = await api.getSupportTicketStatistics();
        const openCount = ticketStats.data?.open || 0;
        setPendingTickets(openCount);
      } catch (error) {
        console.error('Failed to load ticket statistics:', error);
        setPendingTickets(0);
      }
      
      const statsData = licenseStats.data || {};

      setStats({
        licenses: {
          total: Number(statsData.total) || 0,
          active: Number(statsData.active) || 0,
          expiring: Number(statsData.expiring_soon) || 0,
          expired: Number(statsData.expired) || 0,
        },
        pages: {
          total: 0,
          published: 0,
          drafts: 0,
        },
        content: {
          total: 0,
        },
        loading: false,
      });

      const countries = countriesResponse.data || [];
      setTopCountries(countries);
      
      // Calculate total users for percentage calculations
      const total = countries.reduce((sum, country) => sum + country.licenseCount, 0);
      setTotalUsers(total || stats.licenses.total);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setStats({ ...stats, loading: false });
    }
  };

  const loadVisitorCount = async () => {
    try {
      const response = await api.getVisitorCount();
      setVisitorCount(response.data || { today: 0, total: 0 });
    } catch (error) {
      console.error('Failed to load visitor count:', error);
    }
  };

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  if (stats.loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  const activeUsers = stats.licenses.active;
  const passiveUsers = stats.licenses.expired;
  const blockedUsers = stats.licenses.total - stats.licenses.active - stats.licenses.expired;

  return (
    <div className="azimuth-dashboard">
      {/* Full Width Map Container */}
      <div className="world-map-container-full">
        {/* Title and Controls Overlay */}
        <div className="map-overlay-header">
          <h3 className="map-title">Top 5 countries ChesiWMS users</h3>
          
          {/* User Statistics */}
          <div className="stats-overlay text-center d-sm-block d-none">
            <div className="custom-control custom-radio custom-control-inline">
              <input
                type="radio"
                id="radio1"
                name="radio"
                className="custom-control-input radio-primary"
                checked={selectedUserType === 'active'}
                onChange={() => setSelectedUserType('active')}
              />
              <label className="custom-control-label" htmlFor="radio1">
                Active users: {formatNumber(activeUsers)}
              </label>
            </div>
            <div className="custom-control custom-radio custom-control-inline">
              <input
                type="radio"
                id="radio2"
                name="radio"
                className="custom-control-input radio-primary"
                checked={selectedUserType === 'passive'}
                onChange={() => setSelectedUserType('passive')}
              />
              <label className="custom-control-label" htmlFor="radio2">
                Passive users: {formatNumber(passiveUsers)}
              </label>
            </div>
            <div className="custom-control custom-radio custom-control-inline">
              <input
                type="radio"
                id="radio3"
                name="radio"
                className="custom-control-input radio-primary"
                checked={selectedUserType === 'blocked'}
                onChange={() => setSelectedUserType('blocked')}
              />
              <label className="custom-control-label" htmlFor="radio3">
                Blocked users: {formatNumber(blockedUsers)}
              </label>
            </div>
          </div>
        </div>

        {/* Year Selector - Right Side Above Map Statistics */}
        <div className="year-selector-overlay-right">
          <div className="year-selector-circle">
            <button
              className="year-nav-btn"
              onClick={() => setSelectedYear(selectedYear - 1)}
            >
              <i className="fa fa-chevron-left"></i>
            </button>
            <div className="year-display">{selectedYear}</div>
            <button
              className="year-nav-btn"
              onClick={() => setSelectedYear(selectedYear + 1)}
            >
              <i className="fa fa-chevron-right"></i>
            </button>
          </div>
        </div>

        {/* Map Statistics Overlay - Right Side */}
        <div className="map-statistics-overlay">
          <h4>Map statistics</h4>
          {topCountries.length > 0 ? (
            topCountries.map((country, index) => {
              const percentage = totalUsers > 0 ? Math.round((country.licenseCount / totalUsers) * 100) : 0;
              const colors = ['#10b981', '#f59e0b', '#06b6d4', '#ef4444', '#1e40af'];
              return (
                <div key={index} className="map-stat-item">
                  <div className="map-stat-label">{country.country}</div>
                  <div className="map-stat-bar-wrapper">
                    <div className="map-stat-bar" style={{ width: `${percentage}%`, backgroundColor: colors[index] || '#6b7280' }}></div>
                    <span className="map-stat-percent">{percentage}%</span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="map-stat-item">
              <div className="map-stat-label">No data available</div>
            </div>
          )}
          <div className="map-stat-item">
            <div className="map-stat-label">Between 1M and 5M</div>
            <div className="map-stat-bar-wrapper">
              <div className="map-stat-bar" style={{ width: '18%', backgroundColor: '#ef4444' }}></div>
              <span className="map-stat-percent">18%</span>
            </div>
          </div>
          <div className="map-stat-item">
            <div className="map-stat-label">Less than 1M</div>
            <div className="map-stat-bar-wrapper">
              <div className="map-stat-bar" style={{ width: '70%', backgroundColor: '#3b82f6' }}></div>
              <span className="map-stat-percent">70%</span>
            </div>
          </div>
        </div>

        {/* World Map */}
        <div className="world-map-inner">
                <div className="map-controls">
                  <button 
                    className="map-control-btn"
                    onClick={() => setPosition({ coordinates: [0, 20], zoom: 1 })}
                    title="Reset View"
                  >
                    <i className="fa fa-home"></i>
                  </button>
                  <button 
                    className="map-control-btn"
                    onClick={() => setPosition({ ...position, zoom: Math.min(position.zoom * 1.5, 4) })}
                    title="Zoom In"
                  >
                    <i className="fa fa-plus"></i>
                  </button>
                  <button 
                    className="map-control-btn"
                    onClick={() => setPosition({ ...position, zoom: Math.max(position.zoom / 1.5, 0.5) })}
                    title="Zoom Out"
                  >
                    <i className="fa fa-minus"></i>
                  </button>
                </div>
                <div 
                  className="interactive-map-wrapper"
                  onMouseMove={(e) => {
                    if (tooltipContent) {
                      setTooltipPosition({ x: e.clientX, y: e.clientY });
                    }
                  }}
                  onMouseLeave={() => {
                    setTooltipContent('');
                    setHoveredCountry(null);
                  }}
                  onWheel={(e) => {
                    e.preventDefault();
                    const delta = e.deltaY > 0 ? 0.9 : 1.1;
                    setPosition({
                      ...position,
                      zoom: Math.max(0.5, Math.min(4, position.zoom * delta))
                    });
                  }}
                >
                  <ComposableMap
                    projectionConfig={{
                      scale: (147 * 1.3) * position.zoom,
                      center: position.coordinates
                    }}
                    style={{ width: '130%', height: '100%', marginLeft: 0, marginRight: 'auto', display: 'block' }}
                  >
                    <Geographies geography={geoUrl}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const countryName = geo.properties.NAME || geo.properties.NAME_LONG || '';
                          const isTopCountry = topCountries.some(c => 
                            c.country.toLowerCase() === countryName.toLowerCase()
                          );
                          const countryData = topCountries.find(c => 
                            c.country.toLowerCase() === countryName.toLowerCase()
                          );
                          const isHovered = hoveredCountry === countryName;
                          const isSelected = selectedCountry === countryName;

                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill="#2d3748"
                              stroke="#d1d5db"
                              strokeWidth={isTopCountry || isHovered || isSelected ? 0.8 : 0.3}
                              style={{
                                default: {
                                  outline: 'none',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  fill: '#2d3748'
                                },
                                hover: {
                                  fill: '#2d3748',
                                  outline: 'none',
                                  cursor: 'pointer',
                                  stroke: '#d1d5db',
                                  strokeWidth: 0.8
                                },
                                pressed: {
                                  fill: '#2d3748',
                                  outline: 'none',
                                  stroke: '#d1d5db',
                                  strokeWidth: 1
                                }
                              }}
                              onMouseEnter={() => {
                                setHoveredCountry(countryName);
                                if (countryData) {
                                  setTooltipContent(
                                    `${countryName}\nLicenses: ${countryData.licenseCount}\nActive: ${countryData.activeCount}`
                                  );
                                } else {
                                  setTooltipContent(countryName);
                                }
                              }}
                              onMouseLeave={() => {
                                setHoveredCountry(null);
                                if (!tooltipContent.includes('\n')) {
                                  setTooltipContent('');
                                }
                              }}
                              onClick={() => {
                                setSelectedCountry(countryName);
                                if (countryData) {
                                  setTooltipContent(
                                    `${countryName}\nLicenses: ${countryData.licenseCount}\nActive: ${countryData.activeCount}`
                                  );
                                }
                              }}
                            />
                          );
                        })
                      }
                    </Geographies>
                    {topCountries.map((country, index) => (
                      <Marker
                        key={index}
                        coordinates={[country.coordinates.lng, country.coordinates.lat]}
                      >
                        <g
                          onMouseEnter={(e) => {
                            setTooltipContent(
                              `${country.country}\nLicenses: ${country.licenseCount}\nActive: ${country.activeCount}`
                            );
                            setTooltipPosition({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseLeave={() => {
                            if (!hoveredCountry) {
                              setTooltipContent('');
                            }
                          }}
                          onClick={() => setSelectedCountry(country.country)}
                          style={{ cursor: 'pointer' }}
                        >
                          <circle
                            r={12}
                            fill={country.color}
                            stroke="#fff"
                            strokeWidth={selectedCountry === country.country ? 3 : 2}
                            style={{
                              transition: 'all 0.2s ease',
                              filter: selectedCountry === country.country ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))' : 'none'
                            }}
                          />
                          <text
                            textAnchor="middle"
                            y={-15}
                            style={{ 
                              fontFamily: 'system-ui', 
                              fill: selectedCountry === country.country ? '#1e40af' : '#374151', 
                              fontSize: '10px', 
                              fontWeight: 'bold',
                              pointerEvents: 'none'
                            }}
                          >
                            {country.country}
                          </text>
                        </g>
                      </Marker>
                    ))}
                  </ComposableMap>
                  {tooltipContent && (
                    <div
                      className="map-tooltip"
                      style={{
                        left: `${tooltipPosition.x + 10}px`,
                        top: `${tooltipPosition.y - 10}px`,
                        position: 'fixed',
                        pointerEvents: 'none',
                        zIndex: 1000
                      }}
                    >
                      {tooltipContent.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                </div>
        </div>
      </div>

      {/* KPI Cards Section */}
      <div className="row kpi-section">
        {/* Top Row - 6 Small Cards */}
        <div className="col-lg-2 col-md-4 col-sm-6 col-12 bottom-30">
          <div className="kpi-card-small">
            <div className="kpi-icon-small" style={{ backgroundColor: '#3b82f6' }}>
              <i className="fa fa-thumbs-up"></i>
            </div>
            <div className="kpi-content-small">
              <div className="kpi-label-small">LIKES</div>
              <div className="kpi-value-small">{formatNumber(activeUsers)}</div>
            </div>
          </div>
        </div>

        <div className="col-lg-2 col-md-4 col-sm-6 col-12 bottom-30">
          <div className="kpi-card-small">
            <div className="kpi-icon-small" style={{ backgroundColor: '#10b981' }}>
              <i className="fa fa-shopping-cart"></i>
            </div>
            <div className="kpi-content-small">
              <div className="kpi-label-small">SALES</div>
              <div className="kpi-value-small">{formatNumber(stats.licenses.total)}</div>
            </div>
          </div>
        </div>

        <div className="col-lg-2 col-md-4 col-sm-6 col-12 bottom-30">
          <div className="kpi-card-small">
            <div className="kpi-icon-small" style={{ backgroundColor: '#f59e0b' }}>
              <i className="fa fa-ticket-alt"></i>
            </div>
            <div className="kpi-content-small">
              <div className="kpi-label-small">TICKETS</div>
              <div className="kpi-value-small">{formatNumber(pendingTickets)}</div>
            </div>
          </div>
        </div>

        <div className="col-lg-2 col-md-4 col-sm-6 col-12 bottom-30">
          <div className="kpi-card-small">
            <div className="kpi-icon-small" style={{ backgroundColor: '#8b5cf6' }}>
              <i className="fa fa-download"></i>
            </div>
            <div className="kpi-content-small">
              <div className="kpi-label-small">DOWNLOADS</div>
              <div className="kpi-value-small">1,578</div>
          </div>
        </div>
      </div>

        <div className="col-lg-2 col-md-4 col-sm-6 col-12 bottom-30">
          <div className="kpi-card-small">
            <div className="kpi-icon-small" style={{ backgroundColor: '#ec4899' }}>
              <i className="fa fa-users"></i>
            </div>
            <div className="kpi-content-small">
              <div className="kpi-label-small">MEMBERS</div>
              <div className="kpi-value-small">{formatNumber(activeUsers)}</div>
            </div>
          </div>
              </div>

        <div className="col-lg-2 col-md-4 col-sm-6 col-12 bottom-30">
          <div className="kpi-card-small">
            <div className="kpi-icon-small" style={{ backgroundColor: '#06b6d4' }}>
              <i className="fa fa-dollar-sign"></i>
            </div>
            <div className="kpi-content-small">
              <div className="kpi-label-small">PURCHASES</div>
              <div className="kpi-value-small">{formatNumber(stats.licenses.total)}</div>
            </div>
          </div>
        </div>

        {/* Bottom Row - 4 Large Cards */}
        <div className="col-lg-3 col-md-6 col-sm-12 bottom-30">
          <div className="kpi-card-large" style={{ backgroundColor: '#3b82f6' }}>
            <div className="kpi-content-large">
              <div className="kpi-icon-large">
                <i className="fa fa-globe"></i>
              </div>
              <div className="kpi-label-large">USERS GROWTH</div>
              <div className="kpi-value-large">{formatNumber(activeUsers)}</div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-12 bottom-30">
          <div className="kpi-card-large" style={{ backgroundColor: '#ef4444' }}>
            <div className="kpi-content-large">
              <div className="kpi-icon-large">
                <i className="fa fa-user"></i>
              </div>
              <div className="kpi-label-large">VISITS TODAY</div>
              <div className="kpi-value-large">{formatNumber(visitorCount.today)}</div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-12 bottom-30">
          <div className="kpi-card-large" style={{ backgroundColor: '#f59e0b' }}>
            <div className="kpi-content-large">
              <div className="kpi-icon-large">
                <i className="fa fa-shopping-cart"></i>
              </div>
              <div className="kpi-label-large">ORDERS</div>
              <div className="kpi-value-large">{formatNumber(82765)}</div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-12 bottom-30">
          <div className="kpi-card-large" style={{ backgroundColor: '#10b981' }}>
            <div className="kpi-content-large">
              <div className="kpi-icon-large">
                <i className="fa fa-dollar-sign"></i>
              </div>
              <div className="kpi-label-large">TOTAL PROFIT</div>
              <div className="kpi-value-large">${formatNumber(stats.licenses.total * 100)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
